'use client';

import { useEffect } from 'react';
import { toast } from 'sonner';
import { realtime } from '@/lib/websocket';
import { useAuth } from '@/context/AuthContext';
import { useRealtime } from '@/hooks/useRealtime';
import { useRealtimeStore } from '@/store/realtime-store';

interface Props {
  children: React.ReactNode;
}

export default function RealtimeListener({ children }: Props) {
  const { user, isCustomer } = useAuth();

  const upsertSOS = useRealtimeStore((s) => s.upsertSOS);
  const removeSOS = useRealtimeStore((s) => s.removeSOS);
  const upsertIncident = useRealtimeStore((s) => s.upsertIncident);
  const updateGuard = useRealtimeStore((s) => s.updateGuard);
  const updateSupervisor = useRealtimeStore((s) => s.updateSupervisor);
  const clearRealtime = useRealtimeStore((s) => s.clearRealtime);

  useEffect(() => {
    if (!user) {
      realtime.disconnect();
      clearRealtime();
      return;
    }

    const path = isCustomer ? '/ws/customer' : '/ws';
    realtime.connect(path);

    const channels = isCustomer
      ? ['attendance']
      : ['sos', 'incident', 'guard', 'supervisor', 'shift', 'guest', 'analytics'];

    realtime.subscribe(channels);

    return () => {
      realtime.unsubscribe(channels);
    };
  }, [user?.id, isCustomer]);

  // ───────────────── SOS ─────────────────
  useRealtime('sos.triggered', (payload) => {
    upsertSOS(payload);
    toast.error(payload?.message || 'Emergency SOS Alert', { duration: 7000 });
  });

  useRealtime('sos.resolved', (payload) => {
    if (payload?.id) removeSOS(payload.id);
  });

  // ───────────── INCIDENTS ───────────────
  useRealtime('incident.created', (payload) => {
    upsertIncident(payload);
    toast.warning(payload?.title || 'New Incident');
  });

  useRealtime('incident.updated', (payload) => {
    upsertIncident(payload);
  });

  // ───────────── GUARDS ──────────────────
  useRealtime('guard.location', (payload) => updateGuard(payload));
  useRealtime('guard.created', (payload) => updateGuard(payload));
  useRealtime('guard.updated', (payload) => updateGuard(payload));

  // ─────────── SUPERVISORS ───────────────
  useRealtime('supervisor.location', (payload) => updateSupervisor(payload));
  useRealtime('supervisor.created', (payload) => updateSupervisor(payload));
  useRealtime('supervisor.updated', (payload) => updateSupervisor(payload));

  return <>{children}</>;
}